import { InteractionEvent, MfaFactor } from '@logto/schemas';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { getUser, updateRegisterEvent } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import { UserMfaFlow } from '@/types';
import { type MfaFlowState } from '@/types/guard';

import useErrorHandler, { type ErrorHandlers } from './use-error-handler';
import usePreSignInErrorHandler from './use-pre-sign-in-error-handler';
import useStartBackupCodeBinding from './use-start-backup-code-binding';
import useStartTotpBinding from './use-start-totp-binding';
import useStartWebAuthnProcessing from './use-start-webauthn-processing';
import useToast from './use-toast';

const useChooseMfa = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setToast } = useToast();
  const startTotpBinding = useStartTotpBinding();
  const startWebAuthnProcessing = useStartWebAuthnProcessing();
  const startBackupCodeBinding = useStartBackupCodeBinding();
  const replace = false;

  const [errorMessage, setErrorMessage] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [phone, setPhone] = useState<string>();
  const [availableFactors, setAvailableFactors] = useState<string[]>([]);
  const preSignInErrorHandler = usePreSignInErrorHandler({
    interactionEvent: InteractionEvent.Register,
  });
  const handleError = useErrorHandler();
  const { state } = useLocation();
  const asyncGetUser = useApi(getUser);
  const registerEvent = useApi(updateRegisterEvent);
  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  const errorHandlers: ErrorHandlers = useMemo(
    () => ({
      'session.invalid_credentials': (error) => {
        setErrorMessage(error.message);
      },
      ...preSignInErrorHandler,
    }),
    [preSignInErrorHandler]
  );
  const onGetUser = useCallback(
    async (userId: string, isOAuthCallback = false) => {
      // For OAuth callbacks with encrypted user_id, skip initInteraction to preserve session context
      const [error, result] = await asyncGetUser(userId, isOAuthCallback);

      if (error) {
        // Await handleError(error, {
        //   'guard.invalid_input': () => {
        //     setErrorMessage('failed to get email');
        //   },
        // });
        await registerEvent();
        await handleError(error, errorHandlers);
        return;
      }

      if (result) {
        setEmail(result.email);
        setPhone(result.phone);
        setAvailableFactors(result.availableFactors);
      }
    },
    [asyncGetUser, errorHandlers, handleError, registerEvent]
  );

  const handleMfaRedirect = useCallback(
    async (flow: UserMfaFlow, state: MfaFlowState) => {
      const { availableFactors } = state;

      if (availableFactors.length > 1 && flow === UserMfaFlow.MfaBinding) {
        /**
         * Redirect to the MFA binding page if there are multiple available factors.
         */
        navigate({ pathname: `/${flow}` }, { replace, state });
        return;
      }

      /**
       * For verification: the first available factor is the last used factor which is guaranteed by the backend.
       * For binding: the first available factor is the only available factor since we handle the multiple factors case above.
       */
      const factor = availableFactors[0];

      if (!factor) {
        /**
         * This should never happen since we check the available factors' length before handling the redirection.
         */
        setToast(t('error.unknown'));
        return;
      }

      if (factor === MfaFactor.TOTP && flow === UserMfaFlow.MfaBinding) {
        /**
         * Start TOTP binding process if only TOTP is available.
         */
        return startTotpBinding(state, replace);
      }

      if (factor === MfaFactor.WebAuthn) {
        /**
         * Start WebAuthn processing if only TOTP is available.
         */
        return startWebAuthnProcessing(flow, state, replace);
      }

      /**
       * Redirect to the specific MFA factor page.
       */
      navigate({ pathname: `/${flow}/${factor}` }, { replace, state });
    },
    [navigate, replace, setToast, startTotpBinding, startWebAuthnProcessing, t]
  );

  return {
    errorMessage,
    email,
    phone,
    availableFactors,
    clearErrorMessage,
    onGetUser,
    handleMfaRedirect,
  };
};

export default useChooseMfa;
